import { DateTime } from 'luxon';
import { isAttendeeEmail } from './upload.utils';
import { SHEET_DEFECTS } from './workbook.types';
import type {
  CellValue,
  DateColumn,
  RejectedCell,
  SheetDefect,
  ParsedSheet,
  ScheduleEventGroupPayload,
  ScheduleEventPayload,
  SheetGrid,
  SheetLayout,
  TimeOfDay,
} from './workbook.types';

const TITLE_HEADER = 'shift title';
const START_TIME_HEADER = 'start time';
const END_TIME_HEADER = 'end time';

const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const MIDNIGHT: TimeOfDay = { hour: 0, minute: 0 };

const normalizeHeader = (cell: CellValue) =>
  typeof cell === 'string' ? cell.trim().toLowerCase() : '';

const isDateCell = (cell: CellValue): cell is Date =>
  cell instanceof Date && !Number.isNaN(cell.getTime());

export const parseTimeOfDayCell = (cell: CellValue): TimeOfDay | null => {
  if (isDateCell(cell)) return { hour: cell.getUTCHours(), minute: cell.getUTCMinutes() };
  if (typeof cell !== 'string') return null;

  const time = cell.trim().match(TIME_PATTERN);
  if (!time) return null;

  const hour = Number(time[1]);
  const minute = Number(time[2]);
  if (hour > 23 || minute > 59) return null;

  return { hour, minute };
};

export const readSheetLayout = (grid: SheetGrid): SheetLayout | null => {
  const header = grid.rows[0];
  if (!header) return null;

  const indexOfHeader = (name: string) => {
    const index = header.findIndex(cell => normalizeHeader(cell) === name);
    return index === -1 ? null : index;
  };

  const titleIndex = indexOfHeader(TITLE_HEADER);
  if (titleIndex === null) return null;

  const dateColumns = header.reduce<SheetLayout['dateColumns']>((columns, cell, index) => {
    if (isDateCell(cell)) columns.push({ index, date: cell });
    return columns;
  }, []);

  return {
    titleIndex,
    startTimeIndex: indexOfHeader(START_TIME_HEADER),
    endTimeIndex: indexOfHeader(END_TIME_HEADER),
    dateColumns,
  };
};

const readEmail = (cell: CellValue) => {
  if (typeof cell !== 'string') return null;
  const email = cell.trim().toLowerCase();
  return isAttendeeEmail(email) ? email : null;
};

const atTimeInZone = (date: Date, time: TimeOfDay, zone: string, dayOffset = 0) =>
  DateTime.fromObject(
    {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: time.hour,
      minute: time.minute,
    },
    { zone },
  )
    .plus({ days: dayOffset })
    .toUTC()
    .toISO();

const isAfterMidnight = (start: TimeOfDay, end: TimeOfDay) =>
  end.hour * 60 + end.minute <= start.hour * 60 + start.minute;

// A date-column header is a calendar date, not an instant, so it is read off the
// UTC components directly — converting through a zone would shift the day.
const toCalendarDate = (date: Date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;

type Assignment = {
  date: Date;
  userEmail: string;
};

type ParsedRow = {
  group: ScheduleEventGroupPayload | null;
  events: ScheduleEventPayload[];
  rejectedCells: RejectedCell[];
};

const EMPTY_ROW: ParsedRow = { group: null, events: [], rejectedCells: [] };

const defectiveSheet = (sheetName: string, defect: SheetDefect): ParsedSheet => ({
  sheetName,
  defect,
  groups: [],
  events: [],
  rejectedCells: [],
  dateRange: null,
});

const readShiftTitle = (row: CellValue[], titleIndex: number) =>
  typeof row[titleIndex] === 'string' ? String(row[titleIndex]).trim() : '';

// Splits a row's date cells into usable attendees and cells holding something
// else, so the latter can be reported instead of looking like an empty slot.
const readRowCells = (row: CellValue[], dateColumns: DateColumn[], shiftTitle: string) => {
  const assignments: Assignment[] = [];
  const rejectedCells: RejectedCell[] = [];

  dateColumns.forEach(column => {
    const cell = row[column.index];
    const userEmail = readEmail(cell);

    if (userEmail) {
      assignments.push({ date: column.date, userEmail });
      return;
    }

    const value = typeof cell === 'string' ? cell.trim() : '';
    if (value) {
      rejectedCells.push({ shiftTitle, date: toCalendarDate(column.date), value });
    }
  });

  return { assignments, rejectedCells };
};

const parseRow = (row: CellValue[], layout: SheetLayout, orgTimezone: string): ParsedRow => {
  const shiftTitle = readShiftTitle(row, layout.titleIndex);
  if (!shiftTitle) return EMPTY_ROW;

  const { assignments, rejectedCells } = readRowCells(row, layout.dateColumns, shiftTitle);
  if (assignments.length === 0) return { ...EMPTY_ROW, rejectedCells };

  const startTime =
    layout.startTimeIndex === null ? null : parseTimeOfDayCell(row[layout.startTimeIndex]);
  const endTime =
    layout.endTimeIndex === null ? null : parseTimeOfDayCell(row[layout.endTimeIndex]);

  const isAllDay = !startTime || !endTime;
  const start = isAllDay ? MIDNIGHT : startTime!;
  const end = isAllDay ? MIDNIGHT : endTime!;
  const endDayOffset = isAllDay || isAfterMidnight(start, end) ? 1 : 0;

  return {
    group: {
      scheduleEventGroupName: shiftTitle,
      startTime: startTime ?? MIDNIGHT,
      endTime: endTime ?? MIDNIGHT,
      isAllDay,
    },
    events: assignments.map(({ date, userEmail }) => ({
      title: shiftTitle,
      scheduleEventGroupName: shiftTitle,
      attendees: [{ userEmail }],
      startTime: atTimeInZone(date, start, orgTimezone)!,
      endTime: atTimeInZone(date, end, orgTimezone, endDayOffset)!,
      isAllDay,
    })),
    rejectedCells,
  };
};

// The first definition of a title wins; a later row repeating it keeps the
// hours already recorded, because the name is the API's group join key.
const dedupeGroups = (groups: ScheduleEventGroupPayload[]) => {
  const byName = new Map<string, ScheduleEventGroupPayload>();
  groups.forEach(group => {
    if (!byName.has(group.scheduleEventGroupName)) {
      byName.set(group.scheduleEventGroupName, group);
    }
  });
  return [...byName.values()];
};

export const parseSheet = (grid: SheetGrid, orgTimezone: string): ParsedSheet => {
  const layout = readSheetLayout(grid);
  if (!layout) return defectiveSheet(grid.sheetName, SHEET_DEFECTS.MISSING_SHIFT_TITLE);
  if (layout.dateColumns.length === 0) {
    return defectiveSheet(grid.sheetName, SHEET_DEFECTS.NO_DATE_COLUMNS);
  }

  const columnDates = layout.dateColumns.map(column => toCalendarDate(column.date)).sort();
  const rows = grid.rows.slice(1).map(row => parseRow(row, layout, orgTimezone));
  const events = rows.flatMap(row => row.events);

  return {
    sheetName: grid.sheetName,
    defect: events.length === 0 ? SHEET_DEFECTS.NO_SHIFTS : null,
    dateRange: { start: columnDates[0]!, end: columnDates[columnDates.length - 1]! },
    groups: dedupeGroups(rows.flatMap(row => (row.group ? [row.group] : []))),
    events,
    rejectedCells: rows.flatMap(row => row.rejectedCells),
  };
};
